import { Box, Link, Typography } from '@mui/material'
import { useIsMobileWidth } from '@omnea-app/fe-browser-utils/useDetectDevice'
import { type StatusIndicatorValue } from '@omnea-app/fe-legacy/utils/hooks/useSystemStatus'

type StatusIndicatorProps = {
  indicator: StatusIndicatorValue
  description: string
  statusPageUrl: string
  ariaLabel: string
}

const STATUS_COLOR = {
  none: 'bg.success.default',
  minor: 'bg.warning.default',
  major: 'bg.utility.orange.default',
  critical: 'bg.danger.default',
} satisfies Record<StatusIndicatorValue, string>

export const StatusIndicator = ({ indicator, description, statusPageUrl, ariaLabel }: StatusIndicatorProps) => {
  const isMobile = useIsMobileWidth()

  // On mobile, only show the description when there's an active incident —
  // the dot alone is sufficient when everything is operational.
  const displayText = isMobile ? (indicator !== 'none' ? description : null) : description

  return (
    <Link
      href={statusPageUrl}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        transition: 'opacity 0.2s ease-in-out',
        '&:hover': { opacity: 0.7 },
      }}
    >
      <Box
        component="span"
        aria-hidden={true}
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: STATUS_COLOR[indicator],
          flexShrink: 0,
        }}
      />
      {displayText && (
        <Typography
          variant="small-regular"
          sx={{
            fontSize: isMobile ? '10px' : '12px',
            color: 'text.muted',
            lineHeight: 1.2,
          }}
        >
          {displayText}
        </Typography>
      )}
    </Link>
  )
}
